/**
 * Phase 8 · Cluster C-42 — Internal read-only agent tools.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.6
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.6
 *
 * Three tools wired to the existing data services (no new DB queries — we
 * reuse `candidateService.list`, `jobService.list`, `positionTatService.listByOrg`
 * so the RLS / sorting / joining behaviour stays identical to what the UI
 * already ships). Each tool is read-only, caps `limit` at 50 (we do not want
 * the agent dumping the full org pool into a prompt) and returns a DTO the
 * LLM can stringify cheaply.
 *
 * Registration happens at module-evaluation time — each tool below calls
 * the registry's register method from the top level. `./index.ts` re-exports
 * nothing meaningful; it exists purely so `import '@modules/agent/tools'`
 * from `main.tsx` triggers these side effects exactly once per app session.
 */
import { z } from 'zod';
import { toolRegistry } from './registry';
import { candidateService } from '@core/supabase/services/candidateService';
import { jobService, type JobListOptions } from '@core/supabase/services/jobService';
import { positionTatService } from '@core/supabase/services/positionTatService';
import type { Candidate, Job } from '@core/types';
import type { PositionTatRow } from '@core/supabase/services/positionTatService';

// ── Shared DTO shapes ──────────────────────────────────────────────────
// We ship compact DTOs (not the full DB rows) because the LLM consumes
// these as JSON in the next prompt — every extra field costs tokens.

export interface CandidateCardDto {
  id: string;
  fullName: string;
  email: string | null;
  currentTitle: string | null;
  currentCompany: string | null;
  location: string | null;
  skills: string[];
  yearsExp: number | null;
}

export interface JobCardDto {
  id: string;
  title: string;
  status: string;
  location: string | null;
  clientName: string | null;
  openedAt: string | null;
  filledAt: string | null;
  openingsCount: number | null;
}

// The result schema for the requisitions tool reuses the runtime shape
// emitted by `positionTatService.listByOrg` (PositionTatRow). We keep the
// zod schema in sync by hand — if you add a field to PositionTatRow,
// remember to mirror it here or the resultSchema.parse will reject it.

const MAX_LIMIT = 50;

// ── Shared zod helpers ─────────────────────────────────────────────────

const limitSchema = z
  .number()
  .int()
  .positive()
  .max(MAX_LIMIT)
  .optional();

const candidateDtoSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  email: z.string().nullable(),
  currentTitle: z.string().nullable(),
  currentCompany: z.string().nullable(),
  location: z.string().nullable(),
  skills: z.array(z.string()),
  yearsExp: z.number().nullable(),
});

const jobDtoSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: z.string(),
  location: z.string().nullable(),
  clientName: z.string().nullable(),
  openedAt: z.string().nullable(),
  filledAt: z.string().nullable(),
  openingsCount: z.number().nullable(),
});

const positionTatRowSchema = z.object({
  id: z.string(),
  title: z.string(),
  clientName: z.string(),
  ownerUserId: z.string().nullable(),
  ownerName: z.string().nullable(),
  openedAt: z.string().nullable(),
  targetCloseDate: z.string().nullable(),
  closedAt: z.string().nullable(),
  tatDaysTarget: z.number().nullable(),
  tatDaysActual: z.number().nullable(),
  tatBreach: z.boolean(),
  status: z.string(),
});

// ── data.candidates.list ───────────────────────────────────────────────

const candidatesArgsSchema = z.object({
  query: z.string().optional(),
  skills: z.array(z.string()).optional(),
  locationText: z.string().optional(),
  limit: limitSchema,
});
const candidatesResultSchema = z.object({
  candidates: z.array(candidateDtoSchema),
});
type CandidatesArgs = z.infer<typeof candidatesArgsSchema>;
type CandidatesResult = z.infer<typeof candidatesResultSchema>;

function candidateToDto(c: Candidate): CandidateCardDto {
  return {
    id: c.id,
    fullName: c.full_name,
    email: c.email || null,
    currentTitle: c.current_title ?? null,
    currentCompany: c.current_company ?? null,
    location: c.location ?? null,
    skills: Array.isArray(c.parsed_skills) ? c.parsed_skills : [],
    yearsExp: c.years_exp ?? null,
  };
}

toolRegistry.register<CandidatesArgs, CandidatesResult>({
  name: 'data.candidates.list',
  description:
    "List candidates in the org's pool. Optional filters: free-text `query` (matches full_name / email / current_company), `skills` (any-of match against parsed_skills), `locationText` (substring against location). Capped at 50 results.",
  argsSchema: candidatesArgsSchema,
  resultSchema: candidatesResultSchema,
  permissions: ['internal:read'],
  async invoke(ctx, args) {
    if (!ctx.orgId) return { candidates: [] };
    const limit = Math.min(args.limit ?? MAX_LIMIT, MAX_LIMIT);

    // candidateService.list's `search` only covers name/email/company. We
    // keep that server-side to stay consistent with the Candidates list
    // UI, then apply skills / location as a client-side refinement on
    // the (already-capped) page.
    const raw = await candidateService.list(ctx.orgId, {
      search: args.query,
      page: 1,
      pageSize: limit,
    });
    const rows: Candidate[] = Array.isArray(raw) ? raw : raw.data;

    const skillsFilter = (args.skills ?? []).map((s) => s.trim().toLowerCase()).filter(Boolean);
    const locationFilter = args.locationText?.trim().toLowerCase() ?? '';

    const filtered = rows.filter((c) => {
      if (skillsFilter.length > 0) {
        const parsed = (c.parsed_skills ?? []).map((s) => s.toLowerCase());
        const anyMatch = skillsFilter.some((needle) => parsed.some((s) => s.includes(needle)));
        if (!anyMatch) return false;
      }
      if (locationFilter) {
        const loc = (c.location ?? '').toLowerCase();
        if (!loc.includes(locationFilter)) return false;
      }
      return true;
    });

    return { candidates: filtered.slice(0, limit).map(candidateToDto) };
  },
});

// ── data.jobs.list ─────────────────────────────────────────────────────

const jobsArgsSchema = z.object({
  status: z.enum(['active', 'archived', 'all']).optional(),
  ownerUserId: z.string().optional(),
  limit: limitSchema,
});
const jobsResultSchema = z.object({
  jobs: z.array(jobDtoSchema),
});
type JobsArgs = z.infer<typeof jobsArgsSchema>;
type JobsResult = z.infer<typeof jobsResultSchema>;

function jobToDto(j: Job): JobCardDto {
  return {
    id: j.id,
    title: j.title,
    status: j.status,
    location: j.location ?? null,
    clientName: j.client?.name ?? null,
    openedAt: j.opened_at ?? null,
    filledAt: j.filled_at ?? null,
    openingsCount: j.openings_count ?? null,
  };
}

toolRegistry.register<JobsArgs, JobsResult>({
  name: 'data.jobs.list',
  description:
    "List the org's jobs. `status='active'` → open jobs only, `'archived'` → filled/closed, `'all'` → everything (default `active`). Optional `ownerUserId` narrows to a single owner (reserved for future — currently a best-effort filter by created_by). Capped at 50 results.",
  argsSchema: jobsArgsSchema,
  resultSchema: jobsResultSchema,
  permissions: ['internal:read'],
  async invoke(ctx, args) {
    if (!ctx.orgId) return { jobs: [] };
    const limit = Math.min(args.limit ?? MAX_LIMIT, MAX_LIMIT);
    const statusArg = args.status ?? 'active';

    // JobStatus is 'open' | 'on_hold' | 'filled' | 'closed'. We map the
    // agent-friendly vocabulary ('active' / 'archived' / 'all') to that
    // enum without asking the LLM to know both sets.
    const opts: JobListOptions = { page: 1, pageSize: limit };
    if (statusArg === 'active') {
      opts.status = 'open';
    } else if (statusArg === 'archived') {
      // The service accepts a single status; for 'archived' we fetch `closed`
      // and supplement with `filled` via a second call. Both are capped so
      // we stay well under the MAX_LIMIT even when concatenated.
      opts.status = 'closed';
    }

    const primary = await jobService.list(ctx.orgId, opts);
    let rows: Job[] = Array.isArray(primary) ? primary : primary.data;

    if (statusArg === 'archived') {
      const filledRes = await jobService.list(ctx.orgId, {
        page: 1,
        pageSize: limit,
        status: 'filled',
      });
      const filledRows: Job[] = Array.isArray(filledRes) ? filledRes : filledRes.data;
      rows = rows.concat(filledRows);
    }

    if (args.ownerUserId) {
      rows = rows.filter((j) => j.created_by === args.ownerUserId);
    }

    return { jobs: rows.slice(0, limit).map(jobToDto) };
  },
});

// ── data.requisitions.list ─────────────────────────────────────────────

const requisitionsArgsSchema = z.object({
  tatStatus: z.enum(['at_risk', 'breached', 'on_track', 'all']).optional(),
  limit: limitSchema,
});
const requisitionsResultSchema = z.object({
  positions: z.array(positionTatRowSchema),
});
type RequisitionsArgs = z.infer<typeof requisitionsArgsSchema>;
type RequisitionsResult = z.infer<typeof requisitionsResultSchema>;

/**
 * Classify a TAT row into one of three buckets.
 *   breached → past the target close date and still open
 *   at_risk  → open, target in the next 3 days (inclusive)
 *   on_track → everything else
 * Mirrors the bucketing used by the TAT report UI (C-39) — keep in sync
 * when that threshold changes.
 */
const AT_RISK_WINDOW_DAYS = 3;
function classifyTat(row: PositionTatRow): 'at_risk' | 'breached' | 'on_track' {
  if (row.tatBreach) return 'breached';
  if (row.closedAt) return 'on_track';
  if (!row.targetCloseDate) return 'on_track';
  const today = new Date();
  const target = new Date(row.targetCloseDate);
  const diffMs = target.getTime() - today.getTime();
  const diffDays = diffMs / (24 * 60 * 60 * 1000);
  if (diffDays <= AT_RISK_WINDOW_DAYS && diffDays >= 0) return 'at_risk';
  return 'on_track';
}

toolRegistry.register<RequisitionsArgs, RequisitionsResult>({
  name: 'data.requisitions.list',
  description:
    "List the org's open requisitions with TAT status. `tatStatus='at_risk'` → due in the next 3 days, `'breached'` → past target, `'on_track'` → everything else, `'all'` → no filter (default `all`). Capped at 50 rows.",
  argsSchema: requisitionsArgsSchema,
  resultSchema: requisitionsResultSchema,
  permissions: ['internal:read'],
  async invoke(ctx, args) {
    if (!ctx.orgId) return { positions: [] };
    const limit = Math.min(args.limit ?? MAX_LIMIT, MAX_LIMIT);
    const rows = await positionTatService.listByOrg(ctx.orgId);

    const want = args.tatStatus ?? 'all';
    const filtered = want === 'all' ? rows : rows.filter((r) => classifyTat(r) === want);

    return { positions: filtered.slice(0, limit) };
  },
});
